import { useMemo } from "react";
import { ErrorMessage } from "../components/ErrorMessage";
import { Button } from "../components/Button";
import { useFindLogic } from "../features/find/hooks/useFindLogic";
import { HelpText } from "../components/HelpText";
import { calculateEanTotals } from "../features/find/utils/find.utils";
import { EanSearchSection } from "../features/find/components/EanSearchSection";
import { ResultsTable } from "../features/find/components/ResultsTable";
import { WarehouseMap } from "../features/find/components/WarehouseMap";
import { useFeedback } from "../features/find/hooks/useFeedback";
import { useMode } from "../context/ModeContext";
import { Mode } from "../types/Mode";

export const FindPage = () => {
  const { state, actions } = useFindLogic();
  const { copiedLocation, activeShelfIcon, showCopyFeedback, showShelfFeedback } = useFeedback();

  const handleCopy = (location: string, eans: string) => {
    navigator.clipboard.writeText(eans);
    if (navigator.vibrate) navigator.vibrate(50);
    showCopyFeedback(location);
  };

  const handleShelfClick = (location: string) => {
    const shelf = location.substring(0, 7);
    actions.setFilterShelf(state.filterShelf === shelf ? "" : shelf);
    showShelfFeedback(location);
  };

  // COFNAC GDY BEDA EANY STALEJ DLUGOSCI
  // const eanTotals = useMemo(() => calculateEanTotals(state.data), [state.data]);
  const eanTotals = useMemo(() => calculateEanTotals(state.data, state.newData), [state.data, state.newData]);

  const singleAisleEans = useMemo(() => {
    const result = new Set<string>();
    if (!state.data) return result;
    const eanToAisles = new Map<string, Set<string>>();
    state.data.forEach((item) => {
      const location = item.location;
      if (!location || !item.results) return;
      const aisle = location.substring(0, 4);
      item.results.forEach((resultEntry) => {
        const ean = resultEntry.ean;
        if (!ean) return;
        if (!eanToAisles.has(ean)) {
          eanToAisles.set(ean, new Set());
        }
        eanToAisles.get(ean)!.add(aisle);
      });
    });
    eanToAisles.forEach((aisles, ean) => {
      if (aisles.size === 1) {
        result.add(ean);
      }
    });
    return result;
  }, [state.data]);

  const { changeMode } = useMode();

  return (
    <>
      <EanSearchSection
        newData={state.newData}
        eanField={state.eanField}
        eanTotals={eanTotals}
        getEANType={state.getEANType}
        confirmClear={state.confirmClear}
        formError={state.formError}
        isSyncing={state.isSyncing}
        isErrorExiting={state.isErrorExiting}
        hasNotFound={state.hasNotFound}
        actions={{
          addEan: actions.addEan,
          removeEan: actions.removeEan,
          setEanField: actions.setEanField,
          handleClearClick: actions.handleClearClick,
          find: actions.find,
          removeNotFound: actions.removeNotFound
        }}
      />

      {state.data && (
        <HelpText>
          Znalezione EAN-y są zaznaczone kolorem zielonym, a nieznalezione czerwonym. Podwójne kliknięcie EAN-u usuwa go
          z szukanej listy.
        </HelpText>
      )}
      {state.errorMessage && <ErrorMessage>{state.errorMessage}</ErrorMessage>}

      <div
        style={{
          filter: state.isSyncing && state.data ? "blur(4px)" : "none",
          transition: "filter 0.3s ease",
          pointerEvents: state.isSyncing ? "none" : "auto"
        }}
      >
        {((state.data && state.data.length > 0) || state.showSkeleton) && (
          <ResultsTable
            data={state.data || []}
            isLoading={state.showSkeleton}
            filterShelf={state.filterShelf}
            copiedLocation={copiedLocation}
            activeShelfIcon={activeShelfIcon}
            onShelfClick={handleShelfClick}
            onCopy={handleCopy}
            singleAisleEans={singleAisleEans}
          />
        )}
        {state.data &&
          state.data.length > 0 &&
          (!state.filterShelf ? (
            <>
              <Button onClick={actions.changeWarehouse} text="Wizualizacja wyników" />
              <WarehouseMap
                warehouse={state.warehouse}
                warehouseLocations={state.warehouseLocations}
                data={state.data}
              />
            </>
          ) : (
            <>
              <Button
                onClick={() => changeMode(Mode.MANAGE, state.filterShelf)}
                text={"Zarządzaj miejscem: " + state.filterShelf}
              />
            </>
          ))}
      </div>
    </>
  );
};
